import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Query,
} from 'firebase/firestore';
import type { AdminDoc, InvitationDoc } from '@mct/shared';
import { getDb } from '../lib/firebase';

function useCollection<T>(qOrNull: Query<DocumentData> | null): {
  data: T[];
  loading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!qOrNull) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      qOrNull,
      (snap) => {
        setData(snap.docs.map((d) => d.data() as T));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [qOrNull]);

  return { data, loading, error };
}

/**
 * /admins — any signed-in user may read. We filter out revoked admins on
 * the client so a single subscription drives both history and live roster.
 */
export function useAdmins() {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    setQ(
      query(collection(getDb(), 'admins'), orderBy('grantedAt', 'asc')),
    );
  }, []);
  const result = useCollection<AdminDoc>(q);
  return {
    ...result,
    data: result.data.filter((a) => a.revokedAt == null),
  };
}

/**
 * Pending invitations scoped to a project. Rules allow admin + project
 * editor+ to list by `projectId`.
 */
export function useProjectInvitations(projectId: string | null) {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  useEffect(() => {
    if (!projectId) return setQ(null);
    setQ(
      query(
        collection(getDb(), 'invitations'),
        where('projectId', '==', projectId),
        where('status', '==', 'pending'),
      ),
    );
  }, [projectId]);
  return useCollection<InvitationDoc>(q);
}
